import ky, {
  isHTTPError,
  type KyInstance,
  type KyResponse,
  type Options,
} from "ky";
import { DELETION_SCHEDULED_HEADER } from "shared-core/types/account-deletion";
import type { ApiInterface } from "shared-core/types/api";
import type { App } from "vue";

let globalApiInstance: Api | null = null;

export const INVALID_CONTENT_TYPE_ERROR_MESSAGE =
  "Invalid content type. Expected application/json.";

const STATUSES_WITH_OPTIONAL_JSON = new Set([204, 401, 429, 502, 503, 504]);

export class MissingAuthTokenError extends Error {
  constructor() {
    super("Cannot send an authenticated API request without an auth token");
    this.name = "MissingAuthTokenError";
  }
}

export interface ApiConfig {
  prefix?: string;
  timeout?: number;
  retry?: number;
  onUnauthorized?: (request: Request, response: Response) => Promise<void>;
  onDeletionScheduled?: () => Promise<void>;
  onTokenRefresh?: () => Promise<void>;
  getAuthToken?: () => Promise<string | null>;
  getHeaders?: () => Promise<Record<string, string>>;
  fetch?: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
}

export interface ApiResponse<T> {
  readonly responseStatus: number;
  data: Promise<T>;
  readonly headers: Headers;
}

export class Api implements ApiInterface {
  private api: KyInstance;
  private config: ApiConfig;
  private tokenRefreshPromise: Promise<void> | null = null;
  private deletionScheduledPromise: Promise<void> | null = null;

  constructor(config: ApiConfig = {}) {
    this.config = config;

    const options: Options = {
      prefix: config.prefix || "/api",
      timeout: config.timeout || 20000, // 20 seconds
      retry: config.retry ?? 2,
      hooks: {
        beforeRequest: [
          async ({ request }): Promise<void> => {
            // Only set Content-Type for requests that may have a body
            if (request.method !== "GET" && request.method !== "HEAD") {
              request.headers.set("Content-Type", "application/json");
            }

            if (this.config.getAuthToken) {
              let token: string | null;
              try {
                token = await this.config.getAuthToken();
              } catch {
                throw new MissingAuthTokenError();
              }

              if (!token) {
                throw new MissingAuthTokenError();
              }
              request.headers.set("Authorization", token);
            }

            try {
              if (this.config.getHeaders) {
                const headers = await this.config.getHeaders();
                for (const [key, value] of Object.entries(headers)) {
                  request.headers.set(key, value);
                }
              }
            } catch (error) {
              console.warn("Failed to set request headers:", error);
            }
          },
        ],
        beforeError: [
          // Pass error message provided in JSON (if any) to error
          ({ error }): Error => {
            if (
              isHTTPError<{ error?: string }>(error) &&
              typeof error.data === "object" &&
              error.data?.error
            ) {
              error.message = error.data.error;
            }

            return error;
          },
        ],
        afterResponse: [
          // Expect application/json response, except for responses that can be
          // empty or generated by infrastructure outside the JSON API (e.g. 502, 503, 504).
          // (401 is skipped so the token-refresh hook below gets a chance to run)
          // (429 may not have a JSON body)
          ({ response }): KyResponse => {
            if (STATUSES_WITH_OPTIONAL_JSON.has(response.status)) {
              return response;
            }

            const contentType = response.headers.get("content-type");

            if (!contentType || !contentType.includes("application/json")) {
              throw new Error(INVALID_CONTENT_TYPE_ERROR_MESSAGE);
            }

            return response;
          },

          // The refresh puts the deletion gate in front of the app and hits blocked endpoints itself, so a 409 while it runs starts no second one and never waits for it.
          ({ response }): void => {
            if (
              response.headers.get(DELETION_SCHEDULED_HEADER) !== "true" ||
              !this.config.onDeletionScheduled ||
              this.deletionScheduledPromise
            ) {
              return;
            }
            this.deletionScheduledPromise = this.config
              .onDeletionScheduled()
              .catch((error: unknown) => {
                console.error(
                  "Failed to refresh the user after a scheduled deletion",
                  error,
                );
              })
              .finally(() => {
                this.deletionScheduledPromise = null;
              });
          },

          // Interceptor to handle session expiration / token refresh
          async ({
            request,
            options,
            response,
          }): Promise<Response | undefined> => {
            if (response && response.status === 401) {
              // If token refresh is configured, attempt refresh + retry before giving up
              if (this.config.onTokenRefresh) {
                try {
                  // Deduplicate concurrent refresh attempts
                  if (!this.tokenRefreshPromise) {
                    this.tokenRefreshPromise = this.config
                      .onTokenRefresh()
                      .finally(() => {
                        this.tokenRefreshPromise = null;
                      });
                  }
                  await this.tokenRefreshPromise;

                  // Rebuild headers with the fresh token
                  const headers = new Headers(request.headers);
                  const token = await this.config.getAuthToken?.();
                  if (token) {
                    headers.set("Authorization", `${token}`);
                  }
                  if (this.config.getHeaders) {
                    for (const [k, v] of Object.entries(
                      await this.config.getHeaders(),
                    )) {
                      headers.set(k, v);
                    }
                  }

                  // Retry using the configured fetch (bypasses ky to avoid recursive hooks)
                  const fetchFn = this.config.fetch ?? globalThis.fetch;
                  const retryInit: RequestInit = {
                    method: request.method,
                    headers,
                    signal: options.signal || null,
                  };
                  if (
                    request.method !== "GET" &&
                    request.method !== "HEAD" &&
                    options.body
                  ) {
                    retryInit.body = options.body;
                  }
                  const retryResponse = await fetchFn(request.url, retryInit);
                  if (retryResponse.status === 401) {
                    if (this.config.onUnauthorized) {
                      await this.config.onUnauthorized(request, retryResponse);
                    }
                  }
                  return retryResponse;
                } catch (error) {
                  console.warn("Token refresh failed:", error);
                  // Fall through to onUnauthorized
                }
              }

              if (this.config.onUnauthorized) {
                await this.config.onUnauthorized(request, response);
              }
            }
          },
        ],
      },
    };

    if (config.fetch) {
      options.fetch = config.fetch;
    }

    this.api = ky.extend(options);
  }

  async get<T>(url: string, options?: Options): Promise<ApiResponse<T>> {
    return this.handleResponse(await this.api.get<T>(url, options));
  }

  async post<T>(url: string, options?: Options): Promise<ApiResponse<T>> {
    return this.handleResponse(await this.api.post<T>(url, options));
  }

  async delete<T>(url: string, options?: Options): Promise<ApiResponse<T>> {
    return this.handleResponse(await this.api.delete<T>(url, options));
  }

  async put<T>(url: string, options?: Options): Promise<ApiResponse<T>> {
    return this.handleResponse(await this.api.put<T>(url, options));
  }

  private handleResponse<T>(response: KyResponse<T>): ApiResponse<T> {
    if (response.status === 204 || response.status === 429) {
      // @ts-expect-error data is missing, but let's ignore it otherwise we need to handle it in every caller
      return { responseStatus: response.status, headers: response.headers };
    }

    return {
      responseStatus: response.status,
      data: response.json(),
      headers: response.headers,
    };
  }

  install(app: App): void {
    app.provide("api", this);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    globalApiInstance = this;
  }
}

export function useApi(): Api {
  if (!globalApiInstance) {
    throw new Error(
      "Api not initialized. Call app.use(api) before using useApi().",
    );
  }
  return globalApiInstance;
}
